import { TIER_META, roasterFlair } from "../lib/api";
import RoasterAvatar from "./RoasterAvatar";

// 9:16 branded story card, rendered to PNG via html2canvas.
// Uses only html2canvas-safe primitives (solid fills, linear-gradients, text,
// borderRadius, transforms) — no CSS blur/radial-gradient/remote images.
const GRAIN =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";

function roastSize(len) {
  if (len <= 50) return 54;
  if (len <= 100) return 44;
  if (len <= 160) return 36;
  if (len <= 240) return 29;
  return 24;
}

// Per-tier visual identity — mild is a cool singe, no_mercy is a full inferno.
const THEME = {
  mild: {
    base: "#0a0f0c",
    layers: [
      "linear-gradient(155deg, #10b98140 0%, #10b98112 26%, rgba(0,0,0,0) 52%)",
      "linear-gradient(0deg, #060907 8%, rgba(0,0,0,0) 46%)",
    ],
    word: "SINGED", wmOpacity: 0.08, wmSize: 210, grain: 0.04, embers: 0,
  },
  medium: {
    base: "#0f0c06",
    layers: [
      "linear-gradient(150deg, #f59e0b4d 0%, #f59e0b18 28%, rgba(0,0,0,0) 55%)",
      "linear-gradient(0deg, #0a0803 8%, rgba(0,0,0,0) 46%)",
    ],
    word: "ROASTED", wmOpacity: 0.09, wmSize: 200, grain: 0.05, embers: 4,
  },
  savage: {
    base: "#0f0707",
    layers: [
      "linear-gradient(150deg, #ef444459 0%, #ef44441a 24%, rgba(0,0,0,0) 50%)",
      "linear-gradient(300deg, #ef44442e 0%, rgba(0,0,0,0) 40%)",
      "linear-gradient(0deg, #0a0505 8%, rgba(0,0,0,0) 48%)",
    ],
    word: "TORCHED", wmOpacity: 0.11, wmSize: 190, grain: 0.06, embers: 8,
  },
  no_mercy: {
    base: "#0b0603",
    layers: [
      "linear-gradient(145deg, #f9731666 0%, #b91c1c26 22%, rgba(0,0,0,0) 48%)",
      "linear-gradient(305deg, #f973163b 0%, rgba(0,0,0,0) 38%)",
      "linear-gradient(0deg, #0b0603 10%, rgba(120,20,10,0.14) 40%, rgba(0,0,0,0) 62%)",
    ],
    word: "INCINERATED", wmOpacity: 0.12, wmSize: 148, grain: 0.07, embers: 14,
  },
};

export default function ShareCard({ item }) {
  if (item && item.kind === "milestone") return <MilestoneCard item={item} />;
  if (item && item.kind === "progress") return <ProgressCard item={item} />;
  if (item && (item.kind === "verdict" || item.kind === "beforeafter")) return <VerdictCard item={item} />;
  if (item && item.kind === "certificate") return <CertificateCard item={item} />;
  if (item && item.kind === "aivs") return <AiVsCard item={item} />;
  if (item && item.kind === "panel") return <PanelCard item={item} />;
  if (item && item.kind === "hottake") return <HotTakeCard item={item} />;
  if (item && item.kind === "battle") return <BattleCard item={item} />;
  if (item && item.kind === "survival") return <SurvivalCard item={item} />;
  if (item && item.kind === "streak") return <StreakCard item={item} />;
  if (item && item.kind === "quiz") return <QuizCard item={item} />;
  if (item && item.kind === "labs_result") return <LabsResultCard item={item} />;
  const meta = TIER_META[item.tier] || TIER_META.savage;
  const color = meta.color;
  const theme = THEME[item.tier] || THEME.savage;
  const text = item.roast_text || "";
  const roaster = item.roaster_name || "A Roaster";
  const handle = item.roaster_handle || ("@" + roaster.replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 20));
  const initials = (roaster.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "R";
  const wins = item.roaster_wins || 0;
  const flair = roasterFlair(wins);

  return (
    <div
      style={{
        width: 540, height: 960, position: "relative", overflow: "hidden",
        background: theme.base, color: "#f4f4f5",
        fontFamily: "'Manrope', sans-serif", boxSizing: "border-box",
      }}
    >
      {/* tier-themed washes (linear-gradients are html2canvas-safe) */}
      {theme.layers.map((bg, i) => (
        <div key={i} style={{ position: "absolute", inset: 0, background: bg }} />
      ))}
      {/* grain */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: theme.grain }} />
      {/* embers (hotter tiers only) */}
      {Array.from({ length: theme.embers }).map((_, i) => {
        const x = (i * 97 + 30) % 500 + 20;
        const y = (i * 163 + 60) % 820 + 60;
        const s = (i % 3) + 2;
        return <div key={"e" + i} style={{ position: "absolute", left: x, top: y, width: s, height: s, borderRadius: 9999, background: color, opacity: 0.5 }} />;
      })}
      {/* tier watermark word */}
      <div style={{
        position: "absolute", top: 300, left: -40, transform: "rotate(-9deg)",
        fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: theme.wmSize, lineHeight: 1,
        color: color, opacity: theme.wmOpacity, whiteSpace: "nowrap", letterSpacing: "-6px",
      }}>{theme.word}</div>

      {/* content */}
      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {/* top: wordmark + tier */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", textTransform: "uppercase" }}>
              RoastLit
            </span>
          </div>
          <span style={{
            fontSize: 13, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase",
            color: "#09090b", background: color, padding: "6px 12px", borderRadius: 999,
          }}>{meta.label}</span>
        </div>

        {/* middle: the roast */}
        <div style={{ position: "relative" }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 120, lineHeight: 0.6, color: color, opacity: 0.55 }}>“</div>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontWeight: 500,
            fontSize: roastSize(text.length), lineHeight: 1.28, marginTop: 8,
            textShadow: "0 2px 24px rgba(0,0,0,0.6)",
          }}>{text}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
            <RoasterAvatar avatar={item.roaster_avatar} initials={initials} size={48} ring={color} />
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>{roaster}</div>
              <div style={{ fontSize: 14, color: color }}>{handle}</div>
              {wins > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  {flair && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", height: 22, lineHeight: "22px",
                      fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase",
                      color: "#09090b", background: color, padding: "0 10px", borderRadius: 999,
                    }}>{flair}</span>
                  )}
                  <span style={{ fontSize: 13, color: "#a1a1aa" }}>{wins} win{wins > 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* bottom: CTA */}
        <div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.14)", marginBottom: 20 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>
              Get your own roast
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500", letterSpacing: "-0.3px" }}>
              roastlit.app
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Milestone / rank brag card — celebrates a roaster's streak tier.
const MS_THEME = {
  rising: { color: "#f59e0b", label: "RISING ROASTER", blurb: "On the come-up. People are starting to fear the keyboard." },
  top: { color: "#ef4444", label: "TOP ROASTER", blurb: "One of the sharpest tongues on the platform." },
  legend: { color: "#eab308", label: "LEGEND ROASTER", blurb: "Certified menace. A living cautionary tale." },
};

function MilestoneCard({ item }) {
  const t = MS_THEME[item.flairKey] || MS_THEME.rising;
  const color = t.color;
  const roaster = item.roaster_name || "A Roaster";
  const handle = item.roaster_handle || ("@" + roaster.replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 20));
  const initials = (roaster.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "R";
  const wins = item.wins || 0;

  return (
    <div style={{
      width: 540, height: 960, position: "relative", overflow: "hidden",
      background: "#0b0603", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box",
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}4d 0%, ${color}14 30%, rgba(0,0,0,0) 58%)` }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0b0603 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      {/* giant win-count watermark */}
      <div style={{
        position: "absolute", top: 250, left: -20, transform: "rotate(-8deg)",
        fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 360, lineHeight: 1,
        color: color, opacity: 0.1, whiteSpace: "nowrap", letterSpacing: "-14px",
      }}>{wins}</div>

      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {/* top: wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
          <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", textTransform: "uppercase" }}>
            Roast Bounty
          </span>
        </div>

        {/* middle: the rank */}
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "4px", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 14 }}>
            Rank unlocked
          </div>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 78, lineHeight: 0.98,
            color: color, textTransform: "uppercase", letterSpacing: "-1px", textShadow: "0 2px 30px rgba(0,0,0,0.6)",
          }}>{t.label}</div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10, marginTop: 22,
            background: color, color: "#09090b", padding: "10px 18px", borderRadius: 999,
            fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, letterSpacing: "0.5px",
          }}>{wins} WIN{wins === 1 ? "" : "S"}</div>
          <div style={{ fontSize: 20, color: "#d4d4d8", marginTop: 24, lineHeight: 1.35, maxWidth: 400 }}>{t.blurb}</div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 34 }}>
            <RoasterAvatar avatar={item.roaster_avatar} initials={initials} size={52} ring={color} />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{roaster}</div>
              <div style={{ fontSize: 14, color: color }}>{handle}</div>
            </div>
          </div>
        </div>

        {/* bottom: CTA */}
        <div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.14)", marginBottom: 20 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>
              Think you can roast better?
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500", letterSpacing: "-0.3px" }}>
              roastlit.app
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Progress / "on the climb" card — teases how close a roaster is to the next rank.
function ProgressCard({ item }) {
  const color = (MS_THEME[item.nextKey] || MS_THEME.rising).color;
  const roaster = item.roaster_name || "A Roaster";
  const handle = item.roaster_handle || ("@" + roaster.replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 20));
  const initials = (roaster.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "R";
  const wins = item.wins || 0;
  const nextThreshold = item.nextThreshold || 0;
  const toGo = Math.max(0, nextThreshold - wins);
  const pct = Math.max(4, Math.min(100, item.pct || 0));
  const nextLabel = item.nextLabel || "the next rank";

  return (
    <div style={{
      width: 540, height: 960, position: "relative", overflow: "hidden",
      background: "#0b0603", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box",
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}40 0%, ${color}12 30%, rgba(0,0,0,0) 58%)` }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0b0603 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      {/* faint "next" watermark */}
      <div style={{
        position: "absolute", top: 250, left: -20, transform: "rotate(-8deg)",
        fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 280, lineHeight: 1,
        color: color, opacity: 0.08, whiteSpace: "nowrap", letterSpacing: "-12px",
      }}>{toGo}</div>

      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
          <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", textTransform: "uppercase" }}>
            Roast Bounty
          </span>
        </div>

        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "4px", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 14 }}>
            {item.rankLabel ? item.rankLabel + " · climbing" : "On the climb"}
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 96, lineHeight: 0.95, color: "#fff", letterSpacing: "-2px" }}>
            {wins}<span style={{ color: "#52525b", fontSize: 60 }}> / {nextThreshold}</span>
          </div>
          <div style={{ fontSize: 16, color: "#a1a1aa", marginTop: 6, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700 }}>wins</div>

          {/* progress bar */}
          <div style={{ width: 436, height: 16, borderRadius: 999, background: "rgba(255,255,255,0.1)", marginTop: 30, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: color }} />
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 30, color: color, marginTop: 22, textTransform: "uppercase", letterSpacing: "-0.5px" }}>
            {toGo} to go → {nextLabel}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 34 }}>
            <RoasterAvatar avatar={item.roaster_avatar} initials={initials} size={52} ring={color} />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{roaster}</div>
              <div style={{ fontSize: 14, color: color }}>{handle}</div>
            </div>
          </div>
        </div>

        <div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.14)", marginBottom: 20 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>
              Catch me if you can
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500", letterSpacing: "-0.3px" }}>
              roastlit.app
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// "I ASKED 5 STRANGERS" verdict card — one renderer, 3 wording variants
// (roast / judge / validate-default) + a before/after retest variant.
const VERDICT_COPY = {
  roast: { hook: "I ASKED 5 STRANGERS", sub: "TO ROAST ME", accent: "#ff4500" },
  judge: { hook: "I LET 5 STRANGERS", sub: "JUDGE ME", accent: "#22d3ee" },
  validate: { hook: "I ASKED 5 STRANGERS", sub: "TO JUDGE MY IDEA", accent: "#34d399" },
};

function VerdictCard({ item }) {
  const copy = VERDICT_COPY[item.card_variant] || VERDICT_COPY.validate;
  const accent = item.accent || copy.accent;
  const headline = item.headline || {};
  const before = item.before;
  const isBA = item.kind === "beforeafter" && before && before.display;

  return (
    <div style={{
      width: 540, height: 960, position: "relative", overflow: "hidden",
      background: "#08090c", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box",
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${accent}40 0%, ${accent}12 30%, rgba(0,0,0,0) 60%)` }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #08090c 14%, rgba(0,0,0,0) 56%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />

      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {/* top: wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
          <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", textTransform: "uppercase" }}>RoastLit</span>
        </div>

        {/* middle: hook + headline */}
        <div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 46, lineHeight: 1.0, letterSpacing: "-1px", color: "#fff" }}>{copy.hook}</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 46, lineHeight: 1.0, letterSpacing: "-1px", color: accent, marginBottom: 34 }}>{copy.sub}</div>

          {isBA ? (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 18 }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 92, lineHeight: 0.9, color: "#52525b", textDecoration: "line-through" }}>{before.display}</div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, color: accent, paddingBottom: 12 }}>→</div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 140, lineHeight: 0.82, color: accent }}>{headline.display}</div>
            </div>
          ) : (
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 168, lineHeight: 0.82, color: accent, letterSpacing: "-4px" }}>{headline.display}</div>
          )}
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "#a1a1aa", marginTop: 18 }}>{headline.label}</div>
          <div style={{ fontSize: 17, color: "#71717a", marginTop: 8 }}>{item.reviews_count || 5} real humans · verified verdict</div>
        </div>

        {/* bottom: CTA */}
        <div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.14)", marginBottom: 20 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>Get your own verdict</div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500", letterSpacing: "-0.3px" }}>roastlit.app</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Ceremonial certificate — "prove you can take it". Formal serif + gold seal,
// credited to the specific roaster + their independently-earned rank. The
// contrast between the formal frame and savage content is the shareable joke.
function CertificateCard({ item }) {
  const meta = TIER_META[item.tier] || TIER_META.savage;
  const color = meta.color;
  const highStakes = item.submission_kind === "high_stakes";
  const title = highStakes ? "OFFICIAL VERDICT" : `CERTIFIED ${meta.label.toUpperCase()} SURVIVOR`;
  const sub = highStakes ? "Submitted for the panel's honest verdict — and took it." : "Submitted to be roasted by real humans — and lived to post it.";
  const roaster = item.roaster_handle || "@roaster";
  const rank = `${item.roaster_rank || "Starter"}-tier`;
  const gold = "#d4af37";

  return (
    <div style={{
      width: 540, height: 960, position: "relative", overflow: "hidden",
      background: "#0a0a0c", color: "#f4f4f5", fontFamily: "Georgia, 'Times New Roman', serif", boxSizing: "border-box",
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}26 0%, rgba(0,0,0,0) 45%)` }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #08080a 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.05 }} />
      {/* ornamental double border */}
      <div style={{ position: "absolute", inset: 26, border: `2px solid ${gold}`, borderRadius: 6 }} />
      <div style={{ position: "absolute", inset: 34, border: `1px solid ${gold}88`, borderRadius: 4 }} />

      <div style={{ position: "absolute", inset: 0, padding: 64, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", justifyContent: "space-between" }}>
        <div style={{ width: "100%" }}>
          <div style={{ fontSize: 15, letterSpacing: "6px", textTransform: "uppercase", color: gold, marginTop: 8 }}>Certificate of</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 40, letterSpacing: "2px", textTransform: "uppercase", color: "#fff", marginTop: 4 }}>Endurance</div>
        </div>

        <div style={{ width: "100%" }}>
          {/* gold seal */}
          <div style={{ margin: "0 auto 28px", width: 128, height: 128, borderRadius: 9999, background: `radial-gradient(circle at 35% 30%, ${gold}, #8a6d1f)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 0 6px rgba(212,175,55,0.18)` }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 46, color: "#1a1508" }}>{meta.label[0]}</div>
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: title.length > 20 ? 40 : 52, lineHeight: 1.05, color: color, textTransform: "uppercase", letterSpacing: "-0.5px" }}>{title}</div>
          <div style={{ fontSize: 19, color: "#c9c9d0", marginTop: 20, lineHeight: 1.4, maxWidth: 380, marginLeft: "auto", marginRight: "auto", fontStyle: "italic" }}>{sub}</div>
          {item.roast_text && (
            <div style={{ fontSize: 16, color: "#8a8a94", marginTop: 18, lineHeight: 1.4, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>“{String(item.roast_text).slice(0, 120)}{item.roast_text.length > 120 ? "…" : ""}”</div>
          )}
          <div style={{ width: 120, height: 1, background: gold, margin: "30px auto 16px" }} />
          <div style={{ fontSize: 14, letterSpacing: "2px", textTransform: "uppercase", color: "#a1a1aa" }}>Certified by</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 26, color: "#fff", marginTop: 4 }}>{roaster}</div>
          <div style={{ fontSize: 14, color: gold, marginTop: 2, textTransform: "uppercase", letterSpacing: "1.5px" }}>{rank} roaster</div>
        </div>

        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <div style={{ width: 16, height: 16, background: "#ff4500", borderRadius: 4, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 18, letterSpacing: "-0.3px", textTransform: "uppercase" }}>RoastLit</span>
            <span style={{ fontSize: 15, color: "#71717a" }}>· roastlit.app</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sec 14 — AI vs Humans result. The shareable proof that human wit wins.
function AiVsCard({ item }) {
  const humanWon = item.winner === "human";
  const win = humanWon ? "#10b981" : "#a855f7";
  const meta = TIER_META[item.tier] || TIER_META.savage;
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#08090c", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${win}33 0%, rgba(0,0,0,0) 55%)` }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 48, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
          <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit</span>
        </div>
        <div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 40, textTransform: "uppercase", letterSpacing: "-1px", marginBottom: 20 }}>AI vs Humans</div>
          <div style={{ borderRadius: 14, border: "1px solid rgba(255,255,255,0.12)", padding: 22, marginBottom: 16, background: humanWon ? "rgba(255,255,255,0.03)" : "rgba(168,85,247,0.1)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "2px", color: "#a855f7", textTransform: "uppercase" }}>🤖 AI Roast</div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: 24, lineHeight: 1.25, marginTop: 8, color: "#d4d4d8" }}>{String(item.ai_text || "").slice(0, 160)}</div>
          </div>
          <div style={{ borderRadius: 14, border: `2px solid ${humanWon ? "#10b981" : "rgba(255,255,255,0.12)"}`, padding: 22, background: humanWon ? "rgba(16,185,129,0.1)" : "rgba(255,255,255,0.03)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "2px", color: "#10b981", textTransform: "uppercase" }}>👥 Human Roast · {meta.label}</div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: 26, lineHeight: 1.25, marginTop: 8 }}>{String(item.human_text || "").slice(0, 180)}</div>
          </div>
          <div style={{ marginTop: 26, textAlign: "center", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 54, color: win, textTransform: "uppercase" }}>
            {humanWon ? "Humans won 💀" : "AI won 🤖"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>Real humans roast harder</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500" }}>roastlit.app</div>
        </div>
      </div>
    </div>
  );
}

// Sec 17 — Panel Verdict: multiple roasts compiled into one sequential reveal.
function PanelCard({ item }) {
  const meta = TIER_META[item.tier] || TIER_META.savage;
  const color = meta.color;
  const roasts = (item.roasts || []).slice(0, 4);
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#0a0607", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}33 0%, rgba(0,0,0,0) 55%)` }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 44, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, textTransform: "uppercase" }}>RoastLit</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", color: "#09090b", background: color, padding: "6px 12px", borderRadius: 999, textTransform: "uppercase" }}>Panel Verdict</span>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 14, marginTop: 18 }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 34, textTransform: "uppercase", letterSpacing: "-0.5px", marginBottom: 4 }}>The panel weighs in</div>
          {roasts.map((r, i) => (
            <div key={i} style={{ borderLeft: `3px solid ${color}`, paddingLeft: 14 }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: roasts.length > 3 ? 21 : 25, lineHeight: 1.2 }}>{String(r.content_text || "").slice(0, 130)}</div>
              <div style={{ fontSize: 13, color: color, marginTop: 4 }}>{r.roaster_handle || "@roaster"}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>{roasts.length} roasters · one verdict</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500" }}>roastlit.app</div>
        </div>
      </div>
    </div>
  );
}

// Sec 15 — Hot Take result card.
function HotTakeCard({ item }) {
  const majority = item.agree_pct >= 50;
  const color = majority ? "#10b981" : "#ef4444";
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#08090c", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${color}33 0%, rgba(0,0,0,0) 55%)` }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 48, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
          <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit</span>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "4px", textTransform: "uppercase", color: "#a1a1aa", marginBottom: 16 }}>Hot Take</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 46, lineHeight: 1.1, color: "#fff" }}>“{String(item.text || "").slice(0, 120)}”</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 150, lineHeight: 0.9, color: color, marginTop: 30 }}>{item.agree_pct}%</div>
          <div style={{ fontSize: 22, color: "#d4d4d8", marginTop: 6 }}>agree · you're in the <b style={{ color }}>{majority ? "majority" : "minority"}</b></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>Drop your hot take</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500" }}>roastlit.app</div>
        </div>
      </div>
    </div>
  );
}

// Sec 13 — Battle Card: two winning roasts paired side-by-side (display only).
function BattleCard({ item }) {
  const meta = TIER_META[item.tier] || TIER_META.savage;
  const c = meta.color;
  const win = item.winner;
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#08060a", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${c}33 0%, rgba(0,0,0,0) 50%)` }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 44, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: "#ff4500", borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, textTransform: "uppercase" }}>RoastLit</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", color: "#09090b", background: c, padding: "6px 12px", borderRadius: 999, textTransform: "uppercase" }}>Roast-Off</span>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 12 }}>
          {[["challenger", item.challenger_name, item.challenger_roast], ["opponent", item.opponent_name, item.opponent_roast]].map(([side, name, roast], i) => (
            <div key={side}>
              {i === 1 && <div style={{ textAlign: "center", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 34, color: c, margin: "6px 0" }}>VS</div>}
              <div style={{ borderRadius: 14, border: `2px solid ${win === side ? c : "rgba(255,255,255,0.12)"}`, background: win === side ? `${c}14` : "rgba(255,255,255,0.03)", padding: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: c }}>{name} {win === side ? "👑" : ""}</div>
                <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: 22, lineHeight: 1.2, marginTop: 6 }}>{String(roast || "").slice(0, 140)}</div>
              </div>
            </div>
          ))}
          {item.ai_judge && (
            <div style={{ marginTop: 8, textAlign: "center", fontSize: 15, color: "#a855f7", fontWeight: 700 }}>🤖 AI Judge: {item.ai_judge.winner}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", color: "#71717a", fontWeight: 700 }}>Who roasted harder?</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#ff4500" }}>roastlit.app</div>
        </div>
      </div>
    </div>
  );
}


// Sec 9 — RoastLit Survival Score share card.
function SurvivalCard({ item }) {
  const score = Math.max(0, Math.min(100, item.score || 0));
  const brand = "#ff4500";
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#0a0603", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, #f9731655 0%, #b91c1c1a 26%, rgba(0,0,0,0) 54%)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0a0603 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", top: 300, left: 0, right: 0, textAlign: "center", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 340, lineHeight: 1, color: "#ffffff", opacity: 0.06 }}>{score}</div>
      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between", textAlign: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: brand, borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit</span>
          </div>
          <div style={{ marginTop: 14, fontSize: 15, letterSpacing: "5px", textTransform: "uppercase", color: brand }}>Survival Score</div>
        </div>
        <div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 200, lineHeight: 0.9, color: "#fff" }}>{score}</div>
          <div style={{ fontSize: 20, color: "#a1a1aa", marginTop: -6 }}>out of 100</div>
          <div style={{ display: "inline-block", marginTop: 22, fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 40, textTransform: "uppercase", letterSpacing: "1px", color: "#09090b", background: brand, padding: "10px 26px", borderRadius: 999 }}>{item.label || "Fragile"}</div>
          <div style={{ fontSize: 17, color: "#c9c9d0", marginTop: 22 }}>Survived {item.count || 0} roast{item.count === 1 ? "" : "s"} by real humans.</div>
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, color: brand }}>Think you can take it? roastlit.app</div>
      </div>
    </div>
  );
}

// Sec 14 — Arena free personality quiz result card.
// Score Streaks — weekly Survival Score climb recap, built to pull friends in.
function StreakCard({ item }) {
  const brand = "#ff4500";
  const climbed = (item.week_points || 0) > 0;
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#0a0603", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, #f9731666 0%, #b91c1c1f 30%, rgba(0,0,0,0) 58%)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0a0603 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between", textAlign: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: brand, borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit</span>
          </div>
          <div style={{ marginTop: 14, fontSize: 15, letterSpacing: "5px", textTransform: "uppercase", color: brand }}>This week I survived</div>
        </div>
        <div>
          <div style={{ fontSize: 120, lineHeight: 1 }}>🔥</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 150, lineHeight: 0.9, color: "#fff" }}>{item.week_count || 0}</div>
          <div style={{ fontSize: 22, color: "#c9c9d0", marginTop: 4 }}>roast{item.week_count === 1 ? "" : "s"} this week</div>
          {climbed && <div style={{ display: "inline-block", marginTop: 20, fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 34, color: "#09090b", background: brand, padding: "8px 24px", borderRadius: 999 }}>Score +{item.week_points}</div>}
          <div style={{ fontSize: 18, color: "#a1a1aa", marginTop: 22 }}>{item.streak_weeks || 0}-week streak · {item.score || 0}/100 · {item.label || "Fragile"}</div>
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, color: brand }}>Think you'd survive? roastlit.app</div>
      </div>
    </div>
  );
}


// RoastLit Labs — shareable results recap card for a Brand Challenge outcome.
function LabsResultCard({ item }) {
  const brand = "#ff4500";
  const pct = Math.max(0, Math.min(100, item.winner_pct || 0));
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#0a0603", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, #f9731655 0%, #b91c1c1a 26%, rgba(0,0,0,0) 54%)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0a0603 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: brand, borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit <span style={{ color: brand }}>Labs</span></span>
          </div>
          <div style={{ marginTop: 26, fontSize: 15, letterSpacing: "4px", textTransform: "uppercase", color: "#d4af37" }}>{item.brand}</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 46, lineHeight: 1.05, marginTop: 8 }}>{item.question}</div>
        </div>
        <div>
          <div style={{ fontSize: 15, letterSpacing: "3px", textTransform: "uppercase", color: "#a1a1aa" }}>The people decided</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 80, lineHeight: 1, color: "#fff", marginTop: 8 }}>{item.winner || "—"}</div>
          <div style={{ display: "inline-block", marginTop: 14, fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 30, color: "#09090b", background: brand, padding: "6px 20px", borderRadius: 999 }}>{pct}% picked it</div>
          {item.top_comment && <div style={{ fontSize: 19, color: "#c9c9d0", marginTop: 22, fontStyle: "italic", lineHeight: 1.4 }}>“{item.top_comment}”</div>}
          <div style={{ fontSize: 16, color: "#71717a", marginTop: 18 }}>Based on {item.participants || 0} real responses</div>
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, color: brand }}>Run your own · roastlit.app/labs</div>
      </div>
    </div>
  );
}


function QuizCard({ item }) {
  const brand = "#ff4500";
  return (
    <div style={{ width: 540, height: 960, position: "relative", overflow: "hidden", background: "#0b0a12", color: "#f4f4f5", fontFamily: "'Manrope', sans-serif", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, #a855f74d 0%, #6d28d914 28%, rgba(0,0,0,0) 56%)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, #0b0a12 12%, rgba(0,0,0,0) 55%)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url("${GRAIN}")`, opacity: 0.06 }} />
      <div style={{ position: "absolute", inset: 0, padding: 52, display: "flex", flexDirection: "column", justifyContent: "space-between", textAlign: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ width: 22, height: 22, background: brand, borderRadius: 5, transform: "rotate(45deg)" }} />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, textTransform: "uppercase" }}>RoastLit · Arena</span>
          </div>
          <div style={{ marginTop: 14, fontSize: 14, letterSpacing: "4px", textTransform: "uppercase", color: "#c4b5fd" }}>{item.quiz_title || "Quiz result"}</div>
        </div>
        <div>
          <div style={{ fontSize: 90, lineHeight: 1 }}>{item.emoji || "🔥"}</div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 60, lineHeight: 1.02, color: "#fff", marginTop: 14, textTransform: "uppercase" }}>{item.result_title}</div>
          <div style={{ fontSize: 19, color: "#c9c9d0", marginTop: 18, lineHeight: 1.4, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>{item.result_blurb}</div>
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 22, color: brand }}>What's your result? roastlit.app</div>
      </div>
    </div>
  );
}
